import React from 'react';

import { keycodeFromEvent } from 'shared/helpers';


const getMatch = (value, pattern = {}) => {
  const { startText, startOffset } = value;
  if (!startText) return null;

  const { text } = startText;

  let before = (pattern.before
    ? text.slice(0, startOffset).match(pattern.before)
    : null
  );

  let after = (pattern.after
    ? text.slice(startOffset).match(pattern.after)
    : null
  );

  // If both sides, require that both are matched, otherwise null.
  if (pattern.before && pattern.after && !before) after = null;
  if (pattern.before && pattern.after && !after) before = null;

  // Return null unless we have a match.
  if (!before && !after) return null;

  return { before, after };
};



const handleTypeaheadUpdate = ({ eventKey, change, editor, opts }) => {
  const { mapper = {} } = opts;
  const { typeahead } = editor.props;
  const { value } = change;
  const hasActiveTypeahead = !!typeahead.action;

  const entries = Object.entries(mapper);

  // Iterate through mapper, and handle any typeahead state as needed..
  for (const [action, { trigger, before, after, onlyIn }] of entries) {
    // If typeahead is active currently but not for this action, skip..
    // because allow one typeahead action at a time.
    if (hasActiveTypeahead && typeahead.action !== action) {
      continue;
    }

    // Check whether typeahead is even allowed..
    if (typeof onlyIn === 'function' && !onlyIn(value)) {
      return typeahead.off();
    }

    // By now, it means typeahead is either not active at all,
    // or active for this action. Assess for any match.
    const result = getMatch(value, {before, after});

    if (hasActiveTypeahead) {
      // Typeahead is currently active and we have a matching result,
      // so update for the capture.
      if (result) {
        const [ _, capture ] = result.before;  // eslint-disable-line no-unused-vars
        return typeahead.on({ action, capture });
      }

      // Typeahead is currently active but we have no match
      // so it should be updated to be off.
      return typeahead.off();

    } else {
      // Not active right now, but triggered AND there's a match.
      const isTriggered = eventKey === trigger;
      if (isTriggered && result) {
        const [ _, capture ] = result.before;  // eslint-disable-line no-unused-vars
        return typeahead.on({ action, capture });
      }

      // Not triggered or no matching result.. noop.
    }
  }
};



export const AutoTypeahead = (opts) => {
  const wire = {};      // Mutable object to connect editor, plugin and Menu.


  const onKeyDown = (event, change, editor) => {
    wire.change = change;
    wire.editor = editor;
    wire.eventKey = event.key
    wire.halt = false;

    // If typeahead is active, intercept and handle any
    // non printable key behavior..
    const { typeahead } = editor.props;
    const hasActiveTypeahead = !!typeahead.action;

    if (hasActiveTypeahead) {
      const { code, kc } = keycodeFromEvent(event);

      if (
           code === kc.codes.enter
        || code === kc.codes.esc
        || code === kc.codes.tab
        || code === kc.codes.up
        || code === kc.codes.down
      ) {
        event.preventDefault();
      }

      // Lastly, let underlying menu handle key event, if needed.
      if (wire.onKeyDown) {

        // Important: Allow onKeyDown to return, for example
        // to suppress slate default plugin from kicking in.
        return wire.onKeyDown(event);
      }
    }
  }

  /*
  * It's a bit obscure, but here's what's happening:
  * In order to figure out whether typeahead should be on/off and what
  * capture value is, we want the assess this against the slate document
  * state after all changes are applied.
  *
  * With onKeyDown, event is triggered at every key stroke but sadly
  * slate value is *before* the change is applied. With onKeyUp, slate
  * value is *after* the change but sadly event is triggered only if
  * all keys are up; combination of meta or other keys, this makes it
  * very hard to figure typeahead position etc.
  *
  * So the solution is, use onChange event. Sadly, with onChange,
  * editor or event aren't passed in, only change object. Hence,
  * we create 'keydown' object as a closure, and mutate in onKeyDown
  * event since it is triggered before onChange. And inside onChange,
  * we get access to variables like we would in onKeyDown.
  *
  * Convoluted and obscure, but seems to work.
  */
  const onChange = (change) => {
    // bind again with after changes applied..
    wire.change = change;

    const { halt = true, eventKey, editor } = wire;
    if (halt) {
      // onChange doesn't kick in unless explicitly specified
      // in onKeyDown.
      return;
    }

    handleTypeaheadUpdate({ eventKey, change, editor, opts });
  };

  return {
    onChange,
    onKeyDown,

    Typeahead: ({ Menu, ...props }) => {
      if (!Menu) {
        return null;
      }

      return <Menu wire={wire} opts={opts} {...props} />;
    },
  };
}



// WEBPACK FOOTER //
// ./src/components/slate/plugins/AutoTypeahead.js